import { useEffect } from 'react';

interface SEOHelmetProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  ogImageWidth?: string;
  ogImageHeight?: string;
  twitterCard?: string;
  twitterSite?: string;
  noindex?: boolean;
  structuredData?: object | object[]; // JSON-LD structured data
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

/**
 * SEOHelmet component for managing page-specific meta tags
 * Updates document head with SEO-optimized meta information
 * Supports JSON-LD structured data for rich snippets
 * Includes Open Graph, Twitter Cards, and comprehensive meta tags
 */
const SEOHelmet: React.FC<SEOHelmetProps> = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage = 'https://cectrack.com/og-image.png',
  ogImageWidth = '1200',
  ogImageHeight = '630',
  twitterCard = 'summary_large_image',
  twitterSite = '@cectrack',
  noindex = false,
  structuredData,
  author = 'CEC Track',
  publishedTime,
  modifiedTime,
}) => {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = `${title} | CEC Track`;
    }

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Update description
    if (description) {
      updateMetaTag('description', description);
      updateMetaTag('og:description', description, true);
      updateMetaTag('twitter:description', description, true);
    }

    // Update keywords
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Update canonical URL
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]');
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.setAttribute('rel', 'canonical');
        document.head.appendChild(linkElement);
      }
      linkElement.setAttribute('href', canonical);
    }

    // Update Open Graph tags
    if (title) {
      updateMetaTag('og:title', `${title} | CEC Track`, true);
      updateMetaTag('twitter:title', `${title} | CEC Track`, true);
    }
    
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:image:width', ogImageWidth, true);
    updateMetaTag('og:image:height', ogImageHeight, true);
    updateMetaTag('og:image:alt', title || 'CEC Track Logo', true);
    updateMetaTag('og:site_name', 'CEC Track', true);
    updateMetaTag('og:locale', 'en_US', true);
    
    updateMetaTag('twitter:card', twitterCard, true);
    updateMetaTag('twitter:site', twitterSite, true);
    updateMetaTag('twitter:image', ogImage, true);
    updateMetaTag('twitter:image:alt', title || 'CEC Track Logo', true);
    
    if (canonical) {
      updateMetaTag('og:url', canonical, true);
      updateMetaTag('twitter:url', canonical, true);
    }

    // Add author and time-based meta tags
    if (author) {
      updateMetaTag('author', author);
    }
    
    if (publishedTime) {
      updateMetaTag('article:published_time', publishedTime, true);
    }
    
    if (modifiedTime) {
      updateMetaTag('article:modified_time', modifiedTime, true);
    }

    // Add mobile-friendly and language tags
    updateMetaTag('viewport', 'width=device-width, initial-scale=1.0');
    updateMetaTag('format-detection', 'telephone=no');
    updateMetaTag('theme-color', '#667eea');
    
    // Add language tag
    document.documentElement.setAttribute('lang', 'en');

    // Update robots meta tag
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow');
    }

    // Add JSON-LD structured data
    const ldJsonScriptId = 'structured-data-jsonld';
    let ldJsonScript = document.getElementById(ldJsonScriptId) as HTMLScriptElement;
    
    if (structuredData) {
      if (!ldJsonScript) {
        ldJsonScript = document.createElement('script');
        ldJsonScript.id = ldJsonScriptId;
        ldJsonScript.type = 'application/ld+json';
        document.head.appendChild(ldJsonScript);
      }
      
      // Support both single object and array of objects
      const dataArray = Array.isArray(structuredData) ? structuredData : [structuredData];
      ldJsonScript.textContent = JSON.stringify(dataArray.length === 1 ? dataArray[0] : dataArray, null, 2);
    } else if (ldJsonScript) {
      // Remove script if no structured data
      ldJsonScript.remove();
    }

  }, [title, description, keywords, canonical, ogType, ogImage, ogImageWidth, ogImageHeight, twitterCard, twitterSite, noindex, structuredData, author, publishedTime, modifiedTime]);

  return null; // This component doesn't render anything
};

export default SEOHelmet;
